import type { CheckoutSkiAncillaryService, Services, ServiceType } from '@/scopes/checkout/domain/types'
import { useIntl } from '@/intl'
import { getDaysRangeFormatted } from '@/scopes/checkout/components/services/SkiAncillariesService/helpers'
import {
  skiAncillaryDaysRangeByDurationSelector,
  skiAncillaryServiceEditingValueByIdSelector,
} from '@/scopes/checkout/components/services/SkiAncillariesService/selectors'
import { createSkiServiceEvent } from '@/scopes/checkout/domain/analytics/index'
import { useAnalytics } from '@/scopes/common/infrastructure/analytics/hooks'

export function useClickFilterSkiAncillaryService<K extends ServiceType>(service: Services[K] | null, description: string, result?: string | null) {
  const { sendEvent } = useAnalytics()
  const intl = useIntl()

  const sendSkiAncillaryDurationFilterClickEvent = (value: string) => {
    if (!service)
      return

    sendEvent(
      createSkiServiceEvent({
        result: 'Switch number of days',
        type: value,
        description,
        element: 'clickable text',
      }),
    )
  }

  const sendSkiAncillaryRangeFilterClickEvent = (participantId: string, startDate: string) => {
    if (!service)
      return

    const { duration: durationSelected }
      = skiAncillaryServiceEditingValueByIdSelector(service as CheckoutSkiAncillaryService, participantId) || {}
    const availableDaysRange = skiAncillaryDaysRangeByDurationSelector(
      service as CheckoutSkiAncillaryService,
      durationSelected ?? '',
    )
    const { start, end } = availableDaysRange.find(range => range.start === startDate) ?? { start: '', end: '' }

    const { startDay, endDay } = getDaysRangeFormatted(start, end, intl.locale)

    sendEvent(
      createSkiServiceEvent({
        result: 'Change day range',
        type: intl.formatMessage({ id: 'checkout_steps_ancillaries_ski_pass_days_range' }, { startDay, endDay }),
        description,
        element: 'clickable text',
      }),
    )
  }

  const sendSkiAncillaryLevelFilterClickEvent = (value: string) => {
    if (!service)
      return

    sendEvent(
      createSkiServiceEvent({
        result: 'Select level',
        type: value,
        description,
        element: 'clickable text',
      }),
    )
  }

  const sendSkiAncillarySportTypeFilterClickEvent = (value: string) => {
    if (!service)
      return

    sendEvent(
      createSkiServiceEvent({
        result: value,
        type: '',
        description,
        element: 'clickable text',
      }),
    )
  }

  const sendSkiAncillaryOptionFilterClickEvent = (optionId: string) => {
    if (!service)
      return
    const { options } = service as CheckoutSkiAncillaryService
    const selectedOption = options[optionId]
    sendEvent(
      createSkiServiceEvent({
        result: result ?? 'Change ski areas',
        type: selectedOption.label,
        description,
        element: 'clickable text',
      }),
    )
  }

  return {
    sendSkiAncillaryDurationFilterClickEvent,
    sendSkiAncillaryRangeFilterClickEvent,
    sendSkiAncillaryLevelFilterClickEvent,
    sendSkiAncillarySportTypeFilterClickEvent,
    sendSkiAncillaryOptionFilterClickEvent,
  }
}
